import React from "react";
import PropTypes from "prop-types";
import {
  Calendar,
  Users,
  FileText,
  CheckCircle,
  DollarSign,
} from "lucide-react";

const StepReview = ({ onPrev, onSubmit, data, availabilityState }) => {
  // Helper to format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  const currency = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-300">
      <h3 className="text-xl font-semibold text-slate-700 dark:text-slate-300 mb-6 font-alata text-center">
        Review Event Details
      </h3>

      <div className="max-w-3xl mx-auto space-y-6">
        {data.type === "reservation" &&
          (availabilityState?.isChecking || availabilityState?.available === false) && (
            <div className="space-y-2">
              {availabilityState?.isChecking && (
                <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-700 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-200">
                  Rechecking availability for your selected time frame...
                </div>
              )}

              {!availabilityState?.isChecking && availabilityState?.available === false && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200">
                  {availabilityState?.message || "That time frame is no longer available."}
                </div>
              )}
            </div>
          )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Details Card */}
          <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700 shadow-sm">
            <div className="flex items-center space-x-4 mb-4 pb-4 border-b border-slate-200 dark:border-slate-700">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/40 rounded-full">
                {data.type === "reservation" ? (
                  <FileText className="h-6 w-6 text-blue-600" />
                ) : (
                  <Calendar className="h-6 w-6 text-blue-600" />
                )}
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400 uppercase tracking-wider font-semibold">
                  Booking Type
                </p>
                <p className="text-lg font-bold text-slate-900 dark:text-white capitalize">
                  {data.type === "reservation"
                    ? "Venue Reservation"
                    : "Appointment"}
                </p>
              </div>
            </div>

            <dl className="space-y-4">
              {data.eventType && (
                <div>
                  <dt className="text-sm font-medium text-slate-500 dark:text-slate-400">
                    Event Type
                  </dt>
                  <dd className="mt-1 text-base text-slate-900 dark:text-white">
                    {data.eventType}
                  </dd>
                </div>
              )}

              <div>
                <dt className="text-sm font-medium text-slate-500 dark:text-slate-400">
                  Start Date
                </dt>
                <dd className="mt-1 text-base text-slate-900 dark:text-white flex items-center">
                  <Calendar className="h-4 w-4 mr-2 text-slate-400" />
                  {formatDate(data.startDate)}
                </dd>
              </div>

              {data.type === "reservation" && (
                <div>
                  <dt className="text-sm font-medium text-slate-500 dark:text-slate-400">
                    End Date
                  </dt>
                  <dd className="mt-1 text-base text-slate-900 dark:text-white flex items-center">
                    <Calendar className="h-4 w-4 mr-2 text-slate-400" />
                    {formatDate(data.endDate)}
                  </dd>
                </div>
              )}

              <div>
                <dt className="text-sm font-medium text-slate-500 dark:text-slate-400">
                  Guests
                </dt>
                <dd className="mt-1 text-base text-slate-900 dark:text-white flex items-center">
                  <Users className="h-4 w-4 mr-2 text-slate-400" />
                  {data.guests}
                </dd>
              </div>

              {data.details && (
                <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                  <dt className="text-sm font-medium text-slate-500 dark:text-slate-400">
                    Additional Details
                  </dt>
                  <dd className="mt-1 text-sm text-slate-600 dark:text-slate-300 italic">
                    "{data.details}"
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Contact & Price Card */}
          <div className="space-y-6">
            {/* Contact */}
            <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700 shadow-sm">
              <h4 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
                <Users className="h-5 w-5 mr-2 text-blue-500" /> Contact Info
              </h4>
              <div className="text-base text-slate-900 dark:text-white space-y-1">
                <p className="font-bold">
                  {data.contact.firstName} {data.contact.lastName}
                </p>
                <p className="text-slate-600 dark:text-slate-300">
                  {data.contact.email}
                </p>
                <p className="text-slate-600 dark:text-slate-300">
                  {data.contact.phone}
                </p>
              </div>
            </div>

            {/* Price Breakdown (Reservation Only) */}
            {data.type === "reservation" && data.priceBreakdown && (
              <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700 shadow-sm">
                <h4 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
                  <DollarSign className="h-5 w-5 mr-2 text-green-500" /> Price
                  Estimate
                </h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between text-slate-600 dark:text-slate-400">
                    <span>
                      {data.pricingStrategy === "HOURLY"
                        ? `Facility (Hourly – ${data.priceBreakdown.bookedHours ?? ""}h)`
                        : "Facility Rental"}
                    </span>
                    <span>
                      {currency.format(data.priceBreakdown.facilityRental)}
                    </span>
                  </div>
                  <div className="flex justify-between text-slate-600 dark:text-slate-400">
                    <span>
                      Seat Charges ({data.guests} x{" "}
                      {currency.format(
                        data.priceBreakdown.seats?.seatPrice || 0,
                      )}
                      )
                    </span>
                    <span>
                      {currency.format(
                        data.priceBreakdown.seats?.seatRateTotal || 0,
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-slate-600 dark:text-slate-400">
                    <span>Cleaning Fees</span>
                    <span>
                      {currency.format(
                        data.priceBreakdown.facilityCleaningFees,
                      )}
                    </span>
                  </div>
                  {data.priceBreakdown.overtime?.hours > 0 && (
                    <div className="flex justify-between text-orange-600 dark:text-orange-400">
                      <span>
                        Overtime (
                        {data.priceBreakdown.overtime.hours.toFixed(1)}h)
                      </span>
                      <span>
                        {currency.format(
                          data.priceBreakdown.overtime.totalCost,
                        )}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-slate-600 dark:text-slate-400">
                    <span>Add-Ons Total</span>
                    <span>
                      {currency.format(data.priceBreakdown.addOnsTotal)}
                    </span>
                  </div>
                  <div className="border-t border-slate-200 dark:border-slate-700 pt-2 flex justify-between font-bold text-slate-900 dark:text-white">
                    <span>Subtotal</span>
                    <span>{currency.format(data.subTotal)}</span>
                  </div>
                  <div className="flex justify-between text-slate-600 dark:text-slate-400">
                    <span>Tax</span>
                    <span>{currency.format(data.tax)}</span>
                  </div>
                  <div className="border-t-2 border-slate-900 dark:border-white pt-2 flex justify-between text-lg font-extrabold text-blue-600 dark:text-blue-400">
                    <span>Total</span>
                    <span>{currency.format(data.totalPrice)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-between pt-6">
          <button
            type="button"
            onClick={onPrev}
            className="px-6 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white font-medium transition-colors"
          >
            Back
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={
              data.type === "reservation" &&
              (availabilityState?.isChecking || availabilityState?.available === false)
            }
            className={`px-8 py-3 text-white rounded-lg font-bold shadow-lg transition-all flex items-center ${
              data.type === "reservation" &&
              (availabilityState?.isChecking || availabilityState?.available === false)
                ? "bg-slate-400 cursor-not-allowed shadow-none"
                : "bg-green-600 hover:bg-green-700 hover:shadow-xl transform hover:-translate-y-0.5"
            }`}
          >
            <CheckCircle className="w-5 h-5 mr-2" />
            {availabilityState?.isChecking && data.type === "reservation"
              ? "Checking availability..."
              : "Confirm Booking"}
          </button>
        </div>
      </div>
    </div>
  );
};

StepReview.propTypes = {
  onPrev: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  data: PropTypes.shape({
    type: PropTypes.string,
    eventType: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    guests: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    details: PropTypes.string,
    pricingStrategy: PropTypes.string,
    subTotal: PropTypes.number,
    tax: PropTypes.number,
    totalPrice: PropTypes.number,
    contact: PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      email: PropTypes.string,
      phone: PropTypes.string,
    }).isRequired,
    priceBreakdown: PropTypes.shape({
      bookedHours: PropTypes.number,
      facilityRental: PropTypes.number,
      facilityCleaningFees: PropTypes.number,
      addOnsTotal: PropTypes.number,
      seats: PropTypes.shape({
        seatPrice: PropTypes.number,
        seatRateTotal: PropTypes.number,
      }),
      overtime: PropTypes.shape({
        hours: PropTypes.number,
        totalCost: PropTypes.number,
      }),
    }),
  }).isRequired,
  availabilityState: PropTypes.shape({
    isChecking: PropTypes.bool,
    available: PropTypes.bool,
    message: PropTypes.string,
  }),
};

export default StepReview;
